// ---- Toon-look dev panel (F6, plan 23) ---------------------------------------
// Live tuning surface for the custom toon look: rim light (shared RIM uniforms),
// the silhouette outline (shared OUTLINE uniforms), and the gradient ramp's lit
// bands (rebakeRamp). Everything here is CLIENT-VISUAL — no server involvement,
// no wire messages (unlike the F4 weather panel this needs no dev gate). Sliders
// mutate the shared uniform objects directly, so the whole world restyles live;
// the readout prints a copy-paste block of the current values for baking back
// into the defaults (toon.ts RIM/RAMP_DEFAULTS, outline.ts OUTLINE).
//
// Follows weatherdev.ts's panel/slider scaffolding (F4), parked to its left.

import { RIM, RIM_DEFAULTS, TOON_NIGHT, RAMP_DEFAULTS, rebakeRamp } from "./toon.ts";
import { OUTLINE, OUTLINE_DEFAULTS } from "./outline.ts";

let panel: HTMLDivElement;
let readout: HTMLDivElement;
let on = false;

// Ramp state lives here (the texture holds baked bytes, not knobs).
const rampState = {
  bands: [...RAMP_DEFAULTS] as [number, number, number],
  tint: 0x7080c0, // cool blue-violet — the classic cel shadow hue
  tintAmount: 0,
};

const BTN =
  "font:11px ui-monospace, Menlo, monospace; color:#d8cfe3; background:#1e1c24;" +
  "border:1px solid #443a4a; border-radius:5px; padding:2px 7px; cursor:pointer;";

function button(label: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.textContent = label;
  b.style.cssText = BTN;
  b.onclick = onClick;
  return b;
}

function row(parent: HTMLElement, gap = 4): HTMLDivElement {
  const d = document.createElement("div");
  d.style.cssText = `display:flex; flex-wrap:wrap; gap:${gap}px; margin-top:6px; align-items:center;`;
  parent.appendChild(d);
  return d;
}

function slider(
  parent: HTMLElement, label: string, min: number, max: number, step: number,
  value: number, onInput: (v: number) => void,
): HTMLInputElement {
  const wrap = row(parent);
  const lab = document.createElement("span");
  lab.textContent = label;
  lab.style.cssText = "width:64px; display:inline-block;";
  const s = document.createElement("input");
  s.type = "range";
  s.min = String(min); s.max = String(max); s.step = String(step); s.value = String(value);
  s.style.cssText = "flex:1; min-width:110px; accent-color:#9a7fd0;";
  s.oninput = () => { onInput(Number(s.value)); renderReadout(); };
  wrap.append(lab, s);
  return s;
}

/** A labelled `<input type=color>` row (rim + ramp tint share the shape). */
function colorInput(parent: HTMLElement, label: string, hex: string, onInput: (hex: string) => void): void {
  const wrap = row(parent);
  const lab = document.createElement("span");
  lab.textContent = label;
  lab.style.cssText = "width:64px; display:inline-block;";
  const c = document.createElement("input");
  c.type = "color";
  c.value = hex;
  c.oninput = () => { onInput(c.value); renderReadout(); };
  wrap.append(lab, c);
}

function heading(parent: HTMLElement, text: string): void {
  const h = document.createElement("div");
  h.textContent = text;
  h.style.cssText = "margin-top:8px; font-weight:600; color:#b9a8d8;";
  parent.appendChild(h);
}

function bakeRamp(): void {
  rebakeRamp(
    rampState.bands,
    rampState.tintAmount > 0 ? { color: rampState.tint, amount: rampState.tintAmount } : undefined,
  );
}

export function initToonDev(): void {
  panel = document.createElement("div");
  panel.id = "toondev";
  panel.style.cssText =
    "position:fixed; top:14px; right:290px; z-index:7; display:none; width:250px;" +
    "font:11px/1.5 ui-monospace, Menlo, monospace; color:#d8cfe3;" +
    "background:rgba(14,12,18,.88); border:1px solid #443a4a; border-radius:8px;" +
    "padding:8px 10px; user-select:none;";
  panel.addEventListener("pointerdown", (e) => e.stopPropagation());

  const title = document.createElement("div");
  title.textContent = "🎨 toon dev (F6)";
  title.style.cssText = "font-weight:600; margin-bottom:2px;";
  panel.appendChild(title);

  heading(panel, "rim light");
  slider(panel, "strength", 0, 1.5, 0.02, RIM.strength.value, (v) => (RIM.strength.value = v));
  slider(panel, "power", 0.5, 8, 0.1, RIM.power.value, (v) => (RIM.power.value = v));
  slider(panel, "night damp", 0, 1, 0.05, TOON_NIGHT.damp, (v) => (TOON_NIGHT.damp = v));
  colorInput(panel, "color", `#${RIM.color.value.getHexString()}`, (hex) => RIM.color.value.set(hex));

  heading(panel, "outline");
  slider(panel, "thickness", 0, 3, 0.1, OUTLINE.thickness.value, (v) => (OUTLINE.thickness.value = v));
  slider(panel, "threshold", 0.005, 0.1, 0.005, OUTLINE.threshold.value, (v) => (OUTLINE.threshold.value = v));
  slider(panel, "strength", 0, 1, 0.05, OUTLINE.strength.value, (v) => (OUTLINE.strength.value = v));
  slider(panel, "darken", 0, 1, 0.05, OUTLINE.darken.value, (v) => (OUTLINE.darken.value = v));

  heading(panel, "ramp bands");
  slider(panel, "shadow", 0.1, 1, 0.01, rampState.bands[0], (v) => { rampState.bands[0] = v; bakeRamp(); });
  slider(panel, "mid", 0.1, 1, 0.01, rampState.bands[1], (v) => { rampState.bands[1] = v; bakeRamp(); });
  slider(panel, "lit", 0.1, 1, 0.01, rampState.bands[2], (v) => { rampState.bands[2] = v; bakeRamp(); });
  slider(panel, "tint amt", 0, 1, 0.05, rampState.tintAmount, (v) => { rampState.tintAmount = v; bakeRamp(); });
  colorInput(panel, "tint", `#${rampState.tint.toString(16).padStart(6, "0")}`, (hex) => {
    rampState.tint = parseInt(hex.slice(1), 16);
    bakeRamp();
  });

  const actions = row(panel);
  actions.appendChild(button("reset all", () => {
    RIM.strength.value = RIM_DEFAULTS.strength;
    RIM.power.value = RIM_DEFAULTS.power;
    RIM.color.value.set(RIM_DEFAULTS.color);
    TOON_NIGHT.damp = RIM_DEFAULTS.nightDamp;
    OUTLINE.thickness.value = OUTLINE_DEFAULTS.thickness;
    OUTLINE.threshold.value = OUTLINE_DEFAULTS.threshold;
    OUTLINE.strength.value = OUTLINE_DEFAULTS.strength;
    OUTLINE.darken.value = OUTLINE_DEFAULTS.darken;
    rampState.bands = [...RAMP_DEFAULTS]; rampState.tintAmount = 0;
    bakeRamp();
    // Rebuild the panel so every slider snaps back to the restored values.
    panel.remove();
    initToonDev();
    panel.style.display = "block";
    on = true;
    renderReadout();
  }));

  readout = document.createElement("div");
  readout.style.cssText = "white-space:pre; margin-top:6px; color:#a898c8;";
  panel.appendChild(readout);

  document.body.appendChild(panel);
}

export function toggleToonDev(): void {
  on = !on;
  panel.style.display = on ? "block" : "none";
  if (on) renderReadout();
}

// The readout only reflects THIS panel's own sliders (nothing drives these
// values externally, unlike the weather panel), so it's refreshed on each slider
// input — no per-frame poll needed.
function renderReadout(): void {
  if (!readout) return;
  const f = (x: number, d = 2) => x.toFixed(d);
  readout.textContent =
    `— bake these into defaults —\n` +
    `rim: ${f(RIM.strength.value)} pow ${f(RIM.power.value, 1)} #${RIM.color.value.getHexString()}\n` +
    `nightDamp: ${f(TOON_NIGHT.damp)}\n` +
    `outline: t ${f(OUTLINE.thickness.value, 1)} th ${f(OUTLINE.threshold.value, 3)}\n` +
    `  str ${f(OUTLINE.strength.value)} dark ${f(OUTLINE.darken.value)}\n` +
    `ramp: ${rampState.bands.map((b) => f(b)).join("/")}` +
    (rampState.tintAmount > 0 ? `  tint #${rampState.tint.toString(16)} @ ${f(rampState.tintAmount)}` : "");
}
